import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useFormikContext } from 'formik';
import { Select } from 'syngenta-digital-cropwise-react-ui-kit';
import FormGrid, { FormRowConfig } from 'components/FormGrid';
import { COL_SPAN_ONE_THIRD } from 'components/FormGrid/constants';
import ChevronDown from 'assets/chevron_down.svg?react';
import ChevronUp from 'assets/chevron_up.svg?react';
import WarningIcon from 'assets/warning_outlined.svg?react';
import FormInputWithPrecisionDisplay from 'components/PriceUnitField/components/FormInputWithPrecisionDisplay';
import { roundWeatherValue } from 'helpers/weatherDisplayHelpers';

import {
  FormValues,
  WeatherData,
  WeatherMode
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import getDropdownParentWithoutDetaching from 'utilities/getDropdownParentWithoutDetaching';
import filterOptionByNormalizedString from 'screens/Applications&WorkOrders/StepsLayout/Helpers/filterOptions';
import {
  convertTemperature,
  convertWindSpeed,
  TEMPERATURE_UNITS,
  WIND_SPEED_UNITS,
  WIND_DIRECTION_OPTIONS,
  getWindDirectionLabel,
  SOIL_MOISTURE_OPTIONS,
  getSoilMoistureLabel
} from '../../helpers';
import {
  SKY_CONDITION_OPTIONS,
  getSkyConditionLabel
} from '../../helpers/skyConditionMapping';

const WARNING_INPUT_COLOR = '#E99921';
const ONE_DECIMAL_WEATHER_FIELDS = new Set([
  'temperature',
  'windSpeed',
  'humidity'
]);

const RESET_MANUALLY_MODIFIED_FIELDS = {
  skyCondition: false,
  temperature: false,
  humidity: false,
  windSpeed: false,
  windDirection: false,
  soilMoisture: false,
  temperatureUOM: false,
  windSpeedUOM: false
};

export interface WeatherSectionProps {
  prefix: 'start' | 'end';
  originalValues?: WeatherData;
  setHasModifiedWeatherData: (value: boolean) => void;
  resetTrigger?: number; // Increment this to trigger a reset
  autoWeatherEnabled: boolean; // Whether the auto-weather toggle is ON
  lastWeatherMode: React.RefObject<WeatherMode | null>;
  areDatesInFuture: boolean; // Whether application dates are in the future
  apiErrorValue: string | null; // API error value if weather fetch failed
}

const WeatherSection: React.FC<WeatherSectionProps> = ({
  prefix,
  originalValues,
  setHasModifiedWeatherData,
  resetTrigger,
  autoWeatherEnabled,
  lastWeatherMode,
  areDatesInFuture,
  apiErrorValue
}) => {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const weatherValues = useMemo(() => {
    return values.weather?.[prefix] || ({} as WeatherData);
  }, [values.weather, prefix]);

  // Track individual field warning states (only updated on onBlur)
  const [fieldWarnings, setFieldWarnings] = useState({
    skyCondition: false,
    temperature: false,
    humidity: false,
    windSpeed: false,
    windDirection: false,
    soilMoisture: false,
    temperatureUOM: false,
    windSpeedUOM: false
  });

  // Track which fields have been touched (interacted with) by the user
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>(
    {}
  );

  // Dropdown state management for chevron arrows
  const [openDropdowns, setOpenDropdowns] = useState({
    skyCondition: false,
    temperature: false,
    windSpeed: false,
    windDirection: false,
    soilMoisture: false
  });

  const showChevron = (key: keyof typeof openDropdowns): React.ReactNode => {
    return openDropdowns[key] ? (
      <ChevronUp width={18} height={18} />
    ) : (
      <ChevronDown width={18} height={18} />
    );
  };

  const handleDropdownVisibleChange = (
    open: boolean,
    field: keyof typeof openDropdowns
  ): void => {
    setOpenDropdowns(prev => ({
      ...prev,
      [field]: open
    }));
  };

  const setModifiedManuallyFields = useCallback(
    (
      modifiedFields: Partial<
        Record<keyof WeatherData | 'temperatureUOM' | 'windSpeedUOM', boolean>
      >
    ) => {
      // Filter out UI-only tracking fields (temperatureUOM, windSpeedUOM, temperatureSelectedUnit, windSpeedSelectedUnit)
      // and only send actual weather data field names to backend
      // Valid backend fields: soilMoisture, temperatureUnit, windSpeedUnit, temperature, humidity, windDirection, windSpeed, skyCondition
      const uiOnlyFields = new Set([
        'temperatureUOM',
        'windSpeedUOM',
        'temperatureSelectedUnit',
        'windSpeedSelectedUnit'
      ]);
      const validFields = Object.keys(modifiedFields).filter(
        key =>
          modifiedFields[key as keyof WeatherData] === true &&
          !uiOnlyFields.has(key)
      );
      setFieldValue(`weather.${prefix}.manuallyModifiedFields`, validFields);
    },
    [setFieldValue, prefix]
  );
  // Reset field warnings when reset trigger changes
  useEffect(() => {
    if (resetTrigger !== 0) {
      setFieldWarnings(RESET_MANUALLY_MODIFIED_FIELDS);
      setModifiedManuallyFields(RESET_MANUALLY_MODIFIED_FIELDS);
      setHasModifiedWeatherData(false);
      if (values.weather?.mode === WeatherMode.AUTOMATIC && originalValues) {
        setFieldValue(`weather.${prefix}`, originalValues);
      }
      // Also reset touched fields
      setTouchedFields({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetTrigger, values.weather?.mode, setModifiedManuallyFields]);

  // Function to validate all fields against original values
  const validateAllFields = useCallback(() => {
    // Only show warnings when auto-weather is enabled and we have original values to compare against
    // For future dates, we don't have API data so skip validation unless user has entered data
    if (
      lastWeatherMode.current === WeatherMode.MANUAL ||
      values.weather?.mode !== WeatherMode.AUTOMATIC ||
      (!originalValues && !areDatesInFuture && !apiErrorValue)
    ) {
      // If auto-weather is OFF, clear all warnings since we don't need to track modifications
      setFieldWarnings(RESET_MANUALLY_MODIFIED_FIELDS);
      setModifiedManuallyFields(RESET_MANUALLY_MODIFIED_FIELDS);
      setHasModifiedWeatherData(false);
      return;
    }

    let anyFieldModified = false;
    const newFieldWarnings = {
      skyCondition: false,
      temperature: false,
      humidity: false,
      windSpeed: false,
      windDirection: false,
      soilMoisture: false,
      temperatureUOM: false,
      windSpeedUOM: false
    };

    // Check each field against original values
    const fieldsToCheck = [
      {
        name: 'skyCondition',
        value: weatherValues.skyCondition,
        type: 'string'
      },
      { name: 'temperature', value: weatherValues.temperature, type: 'number' },
      { name: 'humidity', value: weatherValues.humidity, type: 'number' },
      { name: 'windSpeed', value: weatherValues.windSpeed, type: 'number' },
      {
        name: 'windDirection',
        value: weatherValues.windDirection,
        type: 'string'
      },
      {
        name: 'soilMoisture',
        value: weatherValues.soilMoisture,
        type: 'string'
      }
    ];

    fieldsToCheck.forEach(({ name, value, type }) => {
      // Default value based on type: '' for strings, 0 for numbers (handles areDatesInFuture case)
      const defaultValue = type === 'number' ? 0 : '';
      const originalValue =
        originalValues?.[name as keyof typeof originalValues] ?? defaultValue;

      // For numbers: treat undefined/null/empty as 0, otherwise convert to number
      // For strings: treat undefined/null as ''
      let normalizedCurrent: number | string;
      if (type === 'number') {
        const raw =
          value === undefined || value === null || value === ''
            ? 0
            : Number(value);
        normalizedCurrent = ONE_DECIMAL_WEATHER_FIELDS.has(name)
          ? (roundWeatherValue(raw) as number)
          : raw;
      } else {
        normalizedCurrent = value || '';
      }
      const normalizedOriginal = (() => {
        if (type !== 'number') return originalValue || '';
        const num = Number(originalValue);
        return ONE_DECIMAL_WEATHER_FIELDS.has(name)
          ? (roundWeatherValue(num) as number)
          : num;
      })();
      const isModified = normalizedOriginal !== normalizedCurrent;
      newFieldWarnings[name as keyof typeof newFieldWarnings] = isModified;

      if (isModified) {
        anyFieldModified = true;
      }
    });

    // Check if UOMs differ from defaults
    // Treat undefined/empty as default (not modified)
    const defaultTempUnit = '°F';
    const defaultWindSpeedUnit = 'MPH';
    const currentTempUnit =
      weatherValues.temperatureSelectedUnit || defaultTempUnit;
    const currentWindSpeedUnit =
      weatherValues.windSpeedSelectedUnit || defaultWindSpeedUnit;

    const tempUOMModified =
      currentTempUnit.toLowerCase() !== defaultTempUnit.toLowerCase();
    const windSpeedUOMModified =
      currentWindSpeedUnit.toUpperCase() !== defaultWindSpeedUnit.toUpperCase();

    newFieldWarnings.temperatureUOM = tempUOMModified;
    newFieldWarnings.windSpeedUOM = windSpeedUOMModified;

    if (tempUOMModified || windSpeedUOMModified) {
      anyFieldModified = true;
    }

    setModifiedManuallyFields(newFieldWarnings);

    setFieldWarnings(newFieldWarnings);
    // Only update global state if any field was touched or if we found modifications
    // This prevents resetting the global state when validating untouched fields
    const anyFieldTouched = Object.keys(touchedFields).length > 0;
    if (anyFieldModified || anyFieldTouched) {
      setHasModifiedWeatherData(anyFieldModified);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    originalValues,
    weatherValues,
    values.weather?.mode,
    setModifiedManuallyFields,
    setHasModifiedWeatherData,
    touchedFields,
    lastWeatherMode
  ]);

  // Validate on mount only
  useEffect(() => {
    validateAllFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-focus Sky Condition field for Start Date weather section
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (prefix === 'start') {
      const focusSelect = () => {
        const selectInput = document.querySelector(
          '.weather-step-form .syt-antd-select input'
        );
        if (
          selectInput &&
          'focus' in selectInput &&
          typeof selectInput.focus === 'function'
        ) {
          (selectInput as HTMLElement).focus();
          return true;
        }
        return false;
      };

      const timer = setTimeout(() => focusSelect(), 300);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [prefix]);

  // Mark a field as touched for validation
  const markFieldAsTouched = (fieldName: string, touched = true) => {
    setTouchedFields(prev => ({ ...prev, [fieldName]: touched }));
  };

  // Check if a specific field is different from its original API value
  const checkFieldModification = (fieldName: string, currentValue: string) => {
    // Don't show warnings when auto-weather is OFF
    if (!autoWeatherEnabled || !originalValues) {
      return;
    }

    // For temperature and wind speed, we need to use the actual stored value, not the display value
    let valueToCompare: number | string = currentValue;
    let originalValue: string | number = String(
      originalValues[fieldName as keyof typeof originalValues]
    );
    if (ONE_DECIMAL_WEATHER_FIELDS.has(fieldName)) {
      valueToCompare = roundWeatherValue(Number(currentValue || 0)) as number;
      originalValue = roundWeatherValue(
        Number(originalValues[fieldName as keyof typeof originalValues] || 0)
      ) as number;
    }

    const normalizedCurrent = ONE_DECIMAL_WEATHER_FIELDS.has(fieldName)
      ? valueToCompare
      : valueToCompare || '';
    const normalizedOriginal = ONE_DECIMAL_WEATHER_FIELDS.has(fieldName)
      ? originalValue
      : originalValue || '';

    const isFieldModified = normalizedOriginal !== normalizedCurrent;

    // Update the specific field warning
    setFieldWarnings(prev => {
      const newWarnings = {
        ...prev,
        [fieldName]: isFieldModified
      };

      // Calculate if ANY field is now modified for the global warning
      const anyFieldModified = Object.keys(originalValues).some(key => {
        if (key === fieldName) {
          return isFieldModified; // Use the current field's new state
        }
        // For other fields, check their current warning state
        return newWarnings[key as keyof typeof newWarnings];
      });

      // Update global state - explicitly set to true or false
      setHasModifiedWeatherData(anyFieldModified);
      setModifiedManuallyFields(newWarnings);

      return newWarnings;
    });
  };

  // Handle temperature unit changes
  const handleTempUnitChange = (newUnit: string) => {
    const currentTemp = Number.parseFloat(weatherValues.temperature);
    let convertedTemp = currentTemp;

    if (!Number.isNaN(currentTemp)) {
      // Convert from current unit to new unit
      convertedTemp = convertTemperature(
        currentTemp,
        weatherValues.temperatureSelectedUnit,
        newUnit
      );

      // Update both the temperature value and the selected unit
      setFieldValue(`weather.${prefix}.temperature`, convertedTemp.toFixed(1));
    }

    setFieldValue(`weather.${prefix}.temperatureSelectedUnit`, newUnit);
    markFieldAsTouched('temperature');

    if (values.weather?.mode === WeatherMode.MANUAL) {
      return;
    }

    // Check if UOM differs from default
    const defaultTempUnit = '°F';
    const isUOMModified =
      newUnit?.toLowerCase() !== defaultTempUnit.toLowerCase();

    // Check if the temperature value (after conversion) differs from original
    const originalTemp = originalValues?.temperature ?? '';
    const isTempValueModified =
      roundWeatherValue(convertedTemp) !==
      roundWeatherValue(Number(originalTemp));

    setFieldWarnings(prev => {
      const newWarnings = {
        ...prev,
        temperature: isTempValueModified,
        temperatureUOM: isUOMModified
      };

      // Update global state
      const anyFieldModified = Object.values(newWarnings).includes(true);
      setHasModifiedWeatherData(anyFieldModified);
      setModifiedManuallyFields(newWarnings);

      return newWarnings;
    });
  };

  // Helper function to handle standard field changes
  const handleFieldChange = (fieldName: string) => (value: string) => {
    setFieldValue(`weather.${prefix}.${fieldName}`, value);
    markFieldAsTouched(fieldName);
    checkFieldModification(fieldName, value);
  };

  // Helper function to handle wind speed unit changes
  const handleWindSpeedUnitChange = (newUnit: string) => {
    const currentWindSpeed = Number.parseFloat(weatherValues.windSpeed);
    let convertedWindSpeed = currentWindSpeed;

    if (!Number.isNaN(currentWindSpeed)) {
      convertedWindSpeed = convertWindSpeed(
        currentWindSpeed,
        weatherValues.windSpeedSelectedUnit,
        newUnit
      );
      setFieldValue(
        `weather.${prefix}.windSpeed`,
        convertedWindSpeed.toFixed(1)
      );
    }
    setFieldValue(`weather.${prefix}.windSpeedSelectedUnit`, newUnit);
    markFieldAsTouched('windSpeed');

    if (values.weather?.mode === WeatherMode.MANUAL) {
      return;
    }

    // Check if UOM differs from default
    const defaultWindSpeedUnit = 'MPH';
    const isUOMModified =
      newUnit?.toUpperCase() !== defaultWindSpeedUnit.toUpperCase();

    // Check if the wind speed value (after conversion) differs from original
    const originalWindSpeed = originalValues?.windSpeed ?? '';
    const isWindSpeedValueModified =
      roundWeatherValue(convertedWindSpeed) !==
      roundWeatherValue(Number(originalWindSpeed));

    setFieldWarnings(prev => {
      const newWarnings = {
        ...prev,
        windSpeed: isWindSpeedValueModified,
        windSpeedUOM: isUOMModified
      };

      // Update global state
      const anyFieldModified = Object.values(newWarnings).includes(true);
      setHasModifiedWeatherData(anyFieldModified);
      setModifiedManuallyFields(newWarnings);

      return newWarnings;
    });
  };

  // Helper function to handle input field blur events
  const handleInputBlur = (fieldName: string) => (val: string = '') => {
    let newValue = val;
    if (ONE_DECIMAL_WEATHER_FIELDS.has(fieldName) && val !== '') {
      newValue = String(
        roundWeatherValue(Number(String(val).replaceAll(',', '')))
      );
    }

    // Always update the field value
    setFieldValue(`weather.${prefix}.${fieldName}`, newValue);

    if (values.weather?.mode === WeatherMode.AUTOMATIC) {
      checkFieldModification(fieldName, newValue);
      if (fieldName === 'temperature') {
        checkFieldModification(
          'temperatureSelectedUnit',
          weatherValues.temperatureSelectedUnit
        );
      }
      if (fieldName === 'windSpeed') {
        checkFieldModification(
          'windSpeedSelectedUnit',
          weatherValues.windSpeedSelectedUnit
        );
      }
    }
  };

  const weatherRows: FormRowConfig[] = [
    {
      id: `${prefix}-weather-row-1`,
      fields: [
        {
          id: `${prefix}SkyCondition`,
          label: 'Sky Condition',
          colSpan: COL_SPAN_ONE_THIRD,
          render: () => {
            const hasWarning =
              !areDatesInFuture && !apiErrorValue && fieldWarnings.skyCondition;

            return (
              <Select
                allowClear
                showSearch
                showArrow
                data-testid={`select-${prefix}SkyCondition`}
                value={weatherValues.skyCondition}
                onChange={handleFieldChange('skyCondition')}
                className="w-full"
                status={hasWarning ? 'warning' : undefined}
                suffixIcon={
                  <div className="flex items-center gap-1">
                    {showChevron('skyCondition')}
                    {!areDatesInFuture && !apiErrorValue && hasWarning && (
                      <WarningIcon style={{ color: WARNING_INPUT_COLOR }} />
                    )}
                  </div>
                }
                filterOption={filterOptionByNormalizedString}
                getPopupContainer={getDropdownParentWithoutDetaching}
                onDropdownVisibleChange={(open: boolean) =>
                  handleDropdownVisibleChange(open, 'skyCondition')
                }
              >
                {SKY_CONDITION_OPTIONS.map(option => (
                  <Select.Option
                    key={`${prefix}SkyCondition-${option.value}`}
                    value={option.value}
                    label={getSkyConditionLabel(option.value)}
                  >
                    {getSkyConditionLabel(option.value)}
                  </Select.Option>
                ))}
              </Select>
            );
          }
        },
        {
          id: `${prefix}Temperature`,
          label: 'Temperature',
          colSpan: COL_SPAN_ONE_THIRD,
          render: () => {
            const hasWarning =
              !areDatesInFuture &&
              !apiErrorValue &&
              (fieldWarnings.temperature || fieldWarnings.temperatureUOM);

            return (
              <FormInputWithPrecisionDisplay
                fieldName="temperature"
                type="number"
                maxPrecision={1}
                round={1}
                className="weather-temp-input"
                object={weatherValues}
                status={hasWarning ? 'warning' : ''}
                onControlledBlur={handleInputBlur('temperature')}
                allowEmptyDisplay
                allowNegative
                inputSuffix={
                  <div className="flex items-center gap-1">
                    <span className="text-gray-500">
                      {weatherValues.temperatureSelectedUnit || '°F'}
                    </span>
                    {hasWarning && (
                      <WarningIcon style={{ color: WARNING_INPUT_COLOR }} />
                    )}
                  </div>
                }
                addonAfter={
                  <Select
                    value={weatherValues.temperatureSelectedUnit || '°F'}
                    onChange={handleTempUnitChange}
                    className={`custom-select ${
                      hasWarning ? 'warning-status' : ''
                    }`}
                    data-testid={`select-${prefix}TemperatureUnit`}
                    onDropdownVisibleChange={(open: boolean) =>
                      handleDropdownVisibleChange(open, 'temperature')
                    }
                    suffixIcon={showChevron('temperature')}
                  >
                    {TEMPERATURE_UNITS.map(unit => (
                      <Select.Option
                        key={`${prefix}TempUnit-${unit.value}`}
                        value={unit.value}
                        label={unit.label}
                      >
                        {unit.label}
                      </Select.Option>
                    ))}
                  </Select>
                }
              />
            );
          }
        },
        {
          id: `${prefix}Humidity`,
          label: 'Relative Humidity',
          colSpan: COL_SPAN_ONE_THIRD,
          render: () => (
            <FormInputWithPrecisionDisplay
              fieldName="humidity"
              type="number"
              maxPrecision={1}
              round={1}
              className="weather-temp-input"
              object={weatherValues}
              status={
                !areDatesInFuture && !apiErrorValue && fieldWarnings.humidity
                  ? 'warning'
                  : ''
              }
              onControlledBlur={handleInputBlur('humidity')}
              allowEmptyDisplay
              inputSuffix={
                <div className="flex items-center gap-1">
                  {weatherValues.humidityUnit && (
                    <span className="text-gray-500">
                      {weatherValues.humidityUnit}
                    </span>
                  )}
                  {!areDatesInFuture &&
                    !apiErrorValue &&
                    fieldWarnings.humidity && (
                      <WarningIcon style={{ color: WARNING_INPUT_COLOR }} />
                    )}
                </div>
              }
            />
          )
        }
      ]
    },
    {
      id: `${prefix}-weather-row-2`,
      fields: [
        {
          id: `${prefix}WindSpeed`,
          label: 'Wind Speed',
          colSpan: COL_SPAN_ONE_THIRD,
          render: () => {
            const hasWarning =
              !areDatesInFuture &&
              !apiErrorValue &&
              (fieldWarnings.windSpeed || fieldWarnings.windSpeedUOM);

            return (
              <FormInputWithPrecisionDisplay
                fieldName="windSpeed"
                type="number"
                maxPrecision={1}
                round={1}
                className="weather-temp-input"
                object={weatherValues}
                status={hasWarning ? 'warning' : ''}
                onControlledBlur={handleInputBlur('windSpeed')}
                allowEmptyDisplay
                inputSuffix={
                  hasWarning ? (
                    <WarningIcon style={{ color: WARNING_INPUT_COLOR }} />
                  ) : (
                    undefined
                  )
                }
                addonAfter={
                  <Select
                    value={weatherValues.windSpeedSelectedUnit || 'MPH'}
                    onChange={handleWindSpeedUnitChange}
                    className={`custom-select ${
                      hasWarning ? 'warning-status' : ''
                    }`}
                    data-testid={`select-${prefix}WindSpeedUnit`}
                    onDropdownVisibleChange={(open: boolean) =>
                      handleDropdownVisibleChange(open, 'windSpeed')
                    }
                    suffixIcon={showChevron('windSpeed')}
                  >
                    {WIND_SPEED_UNITS.map(unit => (
                      <Select.Option
                        key={`${prefix}WindSpeedUnit-${unit.value}`}
                        value={unit.value}
                        label={unit.label}
                      >
                        {unit.label}
                      </Select.Option>
                    ))}
                  </Select>
                }
              />
            );
          }
        },
        {
          id: `${prefix}WindDirection`,
          label: 'Wind Direction',
          colSpan: COL_SPAN_ONE_THIRD,
          render: () => {
            const hasWarning =
              !areDatesInFuture &&
              !apiErrorValue &&
              fieldWarnings.windDirection;

            return (
              <Select
                allowClear
                showSearch
                showArrow
                data-testid={`select-${prefix}WindDirection`}
                value={weatherValues.windDirection}
                onChange={handleFieldChange('windDirection')}
                className="w-full"
                status={hasWarning ? 'warning' : undefined}
                suffixIcon={
                  <div className="flex items-center gap-1">
                    {showChevron('windDirection')}
                    {hasWarning && (
                      <WarningIcon style={{ color: WARNING_INPUT_COLOR }} />
                    )}
                  </div>
                }
                filterOption={filterOptionByNormalizedString}
                getPopupContainer={getDropdownParentWithoutDetaching}
                onDropdownVisibleChange={(open: boolean) =>
                  handleDropdownVisibleChange(open, 'windDirection')
                }
              >
                {WIND_DIRECTION_OPTIONS.map(option => (
                  <Select.Option
                    key={`${prefix}WindDirection-${option.value}`}
                    value={option.value}
                    label={getWindDirectionLabel(option.value)}
                  >
                    {getWindDirectionLabel(option.value)}
                  </Select.Option>
                ))}
              </Select>
            );
          }
        },
        {
          id: `${prefix}SoilMoisture`,
          label: 'Soil Moisture',
          colSpan: COL_SPAN_ONE_THIRD,
          render: () => {
            const hasWarning =
              !areDatesInFuture && !apiErrorValue && fieldWarnings.soilMoisture;

            return (
              <Select
                allowClear
                showSearch
                showArrow
                data-testid={`select-${prefix}SoilMoisture`}
                value={weatherValues.soilMoisture}
                onChange={handleFieldChange('soilMoisture')}
                className="w-full"
                status={hasWarning ? 'warning' : undefined}
                suffixIcon={
                  <div className="flex items-center gap-1">
                    {showChevron('soilMoisture')}
                    {hasWarning && (
                      <WarningIcon style={{ color: WARNING_INPUT_COLOR }} />
                    )}
                  </div>
                }
                filterOption={filterOptionByNormalizedString}
                getPopupContainer={getDropdownParentWithoutDetaching}
                onDropdownVisibleChange={(open: boolean) =>
                  handleDropdownVisibleChange(open, 'soilMoisture')
                }
              >
                {SOIL_MOISTURE_OPTIONS.map(option => (
                  <Select.Option
                    key={`${prefix}SoilMoisture-${option.value}`}
                    value={option.value}
                    label={getSoilMoistureLabel(option.value)}
                  >
                    {getSoilMoistureLabel(option.value)}
                  </Select.Option>
                ))}
              </Select>
            );
          }
        }
      ]
    }
  ];

  return (
    <FormGrid
      rows={weatherRows}
      showErrorLineConditionally
      className="flex flex-col gap-6"
    />
  );
};

export default WeatherSection;
